<template>
  <Modal
    class="repeat-modal"
    :is-displayed="isDisplayed"
    v-bind="$attrs"
    @close="close"
    @clickoutside="close"
  >
    <template #header> Choose Recurrence </template>
    <RecurrentEventParameters
      v-if="recurrence"
      v-model:recurrence="recurrence"
      :event-date="eventDate"
    />
    <div class="buttons">
      <BaseButton kind="important" text="Save" @click="save" />
    </div>
  </Modal>
</template>

<script lang="ts">
import { getDay } from "date-fns/getDay";
import {
  defineComponent,
  nextTick,
  Ref,
  ref,
  watch,
  WatchStopHandle,
} from "vue";

import type { EventTime, Recurrence } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import RecurrentEventParameters from "@/components/RecurrentEventParameters.vue";

import Modal from "./Modal.vue";
import { weekDays } from "./recurrence";

interface RecurrenceTarget {
  recurrence?: Recurrence;
  start: Date;
  time: EventTime;
}

export default defineComponent({
  components: {
    BaseButton,
    RecurrentEventParameters,
    Modal,
  },
  setup() {
    const isDisplayed = ref(false);
    const originalRecurrence = ref<Recurrence>();
    const eventDate = ref<Date>(new Date());
    const recurrence = ref<Recurrence>();
    let unwatchEvent: WatchStopHandle = () => {};
    const reset = () => (recurrence.value = originalRecurrence.value);
    const close = () => {
      reset();
      isDisplayed.value = false;
      nextTick(unwatchEvent);
    };
    const showModal = (event: Ref<RecurrenceTarget | null | undefined>) => {
      if (!event.value) return close();
      isDisplayed.value = true;
      originalRecurrence.value = event.value.recurrence;
      const day = getDay(event.value.start);
      recurrence.value = event.value.recurrence || {
        rule: { period: 1, every: "week", weekdays: { [weekDays[day]]: true } },
      };
      eventDate.value =
        "start_time" in event.value.time
          ? event.value.time.start_time
          : "start_date" in event.value.time
            ? event.value.time.start_date
            : event.value.time.date;
      unwatchEvent = watch(
        recurrence,
        (v) => {
          if (event.value) event.value.recurrence = v;
        },
        { immediate: true }
      );
    };
    const save = () => {
      isDisplayed.value = false;
      unwatchEvent();
    };
    return {
      close,
      eventDate,
      isDisplayed,
      recurrence,
      save,
      showModal,
    };
  },
});
</script>

<style lang="scss" scoped>
.repeat-modal {
  .title {
    margin-inline-start: 4px;
  }

  .buttons {
    display: flex;
    flex-direction: row-reverse;
    column-gap: 8px;
  }
  :deep(.modal) {
    width: 314px;
    min-width: 0;
  }
}
</style>
